import { useRef, useEffect } from "react";
import { Loader2, Save, Check, Ban, Trash2 } from "lucide-react";
import { money, SOURCE_LABELS, ENTRY_STATUS_TONE } from "@/lib/format";
import { computeAmount } from "@/lib/time";
import { cn } from "@/lib/cn";
import { Input } from "@/components/ui-kit/Input";
import { Select } from "@/components/ui-kit/Select";
import { Pill } from "@/components/ui-kit/Pill";
import { TR, TD } from "@/components/ui-kit/Table";

/**
 * One controlled time-entry row (new draft or existing). All field values live in
 * the parent (so the preset line can target the active row); this is presentational.
 */
export function EntryRow({
  rowKey, data, entry, isNew, isDirty, templates, rate, increment, requireReview,
  selected, autoFocus, saving, onField, onFocusHours, onSelect, onSave, onDelete, onStatus,
}) {
  const hoursRef = useRef(null);
  useEffect(() => {
    if (autoFocus && hoursRef.current) {
      hoursRef.current.focus();
      hoursRef.current.select();
    }
  }, [autoFocus]);

  const amount = isNew
    ? computeAmount(Number(data.hours || 0), rate, increment)
    : entry.billable_amount;
  const status = isNew ? (requireReview ? "Draft" : "Approved") : entry.status;
  const source = isNew ? "Workbench" : SOURCE_LABELS[entry.source] || entry.source;

  const labels = Array.isArray(data.labels) ? data.labels : [];
  const addLabel = (label) => {
    if (!label || labels.includes(label)) return;
    const next = [...labels, label];
    onField(rowKey, "labels", next);
    // First label on an empty-duration row auto-fills its default hours (matches
    // today's single-template behavior). Additional labels never stack duration.
    if (next.length === 1) {
      const t = templates.find((x) => x.label === label);
      if (t && t.default_hours != null && !data.hours) onField(rowKey, "hours", String(t.default_hours));
    }
  };
  const removeLabel = (label) => onField(rowKey, "labels", labels.filter((l) => l !== label));

  return (
    <TR
      data-testid={`entry-row-${rowKey}`}
      className={cn(isNew && "bg-status-draft/10", !isNew && isDirty && "bg-status-draft/5")}
    >
      <TD className="w-8">
        {!isNew && (
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onSelect(rowKey)}
            data-testid={`entry-select-${rowKey}`}
            className="h-4 w-4 cursor-pointer accent-primary"
          />
        )}
      </TD>
      <TD className="w-36">
        <Input
          type="date"
          value={data.date}
          onChange={(e) => onField(rowKey, "date", e.target.value)}
          data-testid={`entry-date-${rowKey}`}
          className="h-9"
        />
      </TD>
      <TD className="w-28">
        <Input
          ref={hoursRef}
          type="number"
          step="0.1"
          min="0"
          value={data.hours}
          onChange={(e) => onField(rowKey, "hours", e.target.value)}
          onFocus={(e) => {
            e.target.select();
            onFocusHours(rowKey);
          }}
          data-testid={`entry-hours-${rowKey}`}
          className="h-9 w-full nums"
          placeholder="0.0"
        />
      </TD>
      <TD>
        <Input
          value={data.description}
          onChange={(e) => onField(rowKey, "description", e.target.value)}
          data-testid={`entry-desc-${rowKey}`}
          placeholder="Legal service performed…"
          className="h-9"
        />
      </TD>
      <TD className="w-52">
        <div className="flex flex-wrap gap-1" data-testid={`entry-labels-${rowKey}`}>
          {labels.map((l) => (
            <span
              key={l}
              data-testid={`entry-label-chip-${rowKey}-${l}`}
              className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary"
            >
              {l}
              <button
                type="button"
                onClick={() => removeLabel(l)}
                data-testid={`entry-label-remove-${rowKey}-${l}`}
                className="text-primary/70 hover:text-primary"
                title="Remove label"
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <Select
          value=""
          onChange={(e) => { addLabel(e.target.value); e.target.value = ""; }}
          data-testid={`entry-template-${rowKey}`}
          className="mt-1 h-9"
        >
          <option value="">+ Add task…</option>
          {templates
            .filter((t) => !labels.includes(t.label))
            .map((t) => (
              <option key={t.id} value={t.label}>
                {t.label}
              </option>
            ))}
        </Select>
      </TD>
      <TD className="nums text-right text-foreground" data-testid={`entry-amount-${rowKey}`}>
        {money(amount)}
      </TD>
      <TD>
        <Pill tone={ENTRY_STATUS_TONE[status] || "neutral"} data-testid={`entry-status-${rowKey}`}>
          {isNew ? `→ ${status}` : status}
        </Pill>
      </TD>
      <TD className="whitespace-nowrap text-muted-foreground">{source}</TD>
      <TD>
        <div className="flex items-center justify-end gap-1">
          {(isNew || isDirty) && (
            <button
              type="button"
              onClick={() => onSave(rowKey)}
              disabled={saving}
              data-testid={`entry-save-${rowKey}`}
              className="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/10 px-2 py-1 text-xs font-medium text-primary hover:bg-primary/20"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save
            </button>
          )}
          {!isNew && !isDirty && (
            <span className="inline-flex items-center gap-1 text-xs text-status-paid" data-testid={`entry-saved-${rowKey}`}>
              <Check className="h-3.5 w-3.5" /> Saved
            </span>
          )}
          {!isNew && status !== "Approved" && (
            <button
              type="button"
              onClick={() => onStatus(rowKey, "Approved")}
              data-testid={`entry-approve-${rowKey}`}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-status-paid"
              title="Approve"
            >
              <Check className="h-4 w-4" />
            </button>
          )}
          {!isNew && status !== "Excluded" && (
            <button
              type="button"
              onClick={() => onStatus(rowKey, "Excluded")}
              data-testid={`entry-exclude-${rowKey}`}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-destructive"
              title="Exclude"
            >
              <Ban className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => onDelete(rowKey)}
            data-testid={`entry-delete-${rowKey}`}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-destructive"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </TD>
    </TR>
  );
}
